<template>
    <div class="mx-auto">
        <div class="card">
            <div class="card-header bg-primary text-white">
                <page-title :title="($t(titleKey))"></page-title>
            </div>

            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-3" role="toolbar">
                    <div class="form-inline pl-2 col-lg-6 col-md-12">
                        <button v-if="hasCreateRights" type="button" class="btn btn-primary" @click.stop="createBtnClick">
                            {{ $t('buttons.create.btnText') }}
                        </button>

                    </div>

                    <div class="input-group col-lg-6 col-md-12">
                        <input v-model="searchQuery" v-on:keyup.enter.stop="massSearch" v-on:keyup.esc.stop="clearFilters" type="text" class="form-control" aria-label="Mass search input" aria-describedby="massSearch">
                        <button type="button" class="btn btn-secondary" @click.stop="massSearch">
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button type="button" class="btn btn-secondary"
                                @click.stop="clearFilters">
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size='gridOptions.pageable.size'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError"
                                  :requestEnd="datasorceRequestEnd">
                </kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :navigatable="gridOptions.navigatable"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :dataBound="dataBound">
                    <kendo-grid-column :field="'id'"
                                       :filterable="false"
                                       :title="$t('registers.staticRegistersForDropdown.gridHeaders.id')"
                                       v-html="rawHtml">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'staticRegisterName'"
                                       :title="$t('registers.staticRegistersForDropdown.gridHeaders.staticRegisterName')">
                    </kendo-grid-column>

                    <kendo-grid-column :field="'displayMember'"
                                       :title="$t('registers.staticRegistersForDropdown.gridHeaders.displayMember')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'description'"
                                       :title="$t('registers.staticRegistersForDropdown.gridHeaders.description')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'active'"
                                       :title="$t('registers.staticRegistersForDropdown.gridHeaders.active')"
                                       :template="activeCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'allowAnonymousAccesss'"
                                       :title="$t('registers.staticRegistersForDropdown.gridHeaders.allowAnonymousAccesss')"
                                       :template="allowAnonymousAccesssCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column :template="btnsTemplate"
                                       field="btnsColumn"
                                       :title="$t('actions')"
                                       v-if="showBtns">
                    </kendo-grid-column>
                </kendo-grid>
            </div>
        </div>
        <CreateModal :show="showCreate" :profileManagement="true" @onCreate="onCreate" @onClose="onCreateClose"></CreateModal>
        <EditModal :show="showEdit" v-model="model" :profileManagement="true" @onClose="onEditClose" @onSave="onPersonEdit"></EditModal>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
    import CreateModal from "@/components/administration/staticRegister/create.modal";
    import EditModal from "@/components/administration/staticRegister/edit.modal";
    import staticRegisterService from '@/services/staticRegister.service';
    import PageTitle from '@/components/common/PageTitle';
    import { mapGetters } from 'vuex'

    export default {
        components: {
            CreateModal,
            EditModal,
            PageTitle
        },
        props: {
            showBtns: {
                default: true,
                type: Boolean
            }
        },
        data() {
            return {
                componentName: "staticRegistersForDropdown",
                titleKey: 'registers.staticRegistersForDropdown.title',
                rawHtml: "",
                defaultGridRows: this.$store.getters.defaultGridRows,
                working: false,
                showCreate: false,
                showEdit: false,
                model: null,
                searchQuery: '',
                schemaModelFields: {
                    id: { name: 'id', type: 'number', editable: false, nullable: true, visible: true },
                    registerId: { name: 'registerId', type: 'number', editable: false, nullable: true, visible: true },
                    staticRegisterName: { name: 'staticRegisterName', type: 'string', validation: { required: true }, editable: true, nullable: false, visible: true },
                    displayMember: { name: 'displayMember', type: 'string', validation: { required: true }, editable: true, nullable: false, visible: true },
                    description: { name: 'description', type: 'string', editable: true, nullable: true, visible: true },
                    schema: { name: 'schema', type: 'string', editable: true, nullable: true, visible: true },
                    deactivated: { name: 'deactivated', type: 'boolean', editable: false, nullable: true, visible: true },
                    active: { name: 'active', type: 'boolean', editable: false, nullable: true, visible: true },
                    allowAnonymousAccesss: { name: 'allowAnonymousAccesss', type: 'boolean', editable: false, nullable: true, visible: true }
                },
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/dynamicregistersadministration/listStaticRegisterForDropDown',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                }
            }
        },
        methods: {
            dataBound(e) {

            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                })
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    // Викаме POST action за листване на номенклатурите и трябва да сериализираме
                    // options. Така се предава информация за take, skip , page и списък с обекти
                    // описващи сортирането
                    return kendo.stringify(options);
                }
            },
            activeCellTemplate: function (dataItem) {
                let templateHtml =
                    '<div class="text-center"><span class="badge ' + (dataItem.active ? 'badge-success' : 'badge-danger') + '">' + kendo.htmlEncode(dataItem.active ? this.$t('yes') : this.$t('no')) + '</span></div>';

                return templateHtml;
            },
            allowAnonymousAccesssCellTemplate: function(dataItem) {
                let templateHtml =
                    '<div class="text-center"><span class="badge ' + (dataItem.allowAnonymousAccesss ? 'badge-success' : 'badge-light') + '">' + kendo.htmlEncode(dataItem.allowAnonymousAccesss ? this.$t('yes') : this.$t('no')) + '</span></div>';

                return templateHtml;
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: [
                            {
                                name: 'edit',
                                text: '',
                                tooltip: this.$t('buttons.edit.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'fas fa-edit',
                                show: this.hasUpdateRights,
                                clickHandler: this.showEditModal
                            },
                            {
                                name: "deactivation",
                                text: "",
                                tooltip: this.$t('buttons.deactivate.btnText'),
                                class: 'btn btn-danger',
                                icon: "fas fa-lock",
                                show: this.hasDeleteRights,
                                clickHandler: this.deactivateBtnClick
                            }, {
                                name: 'activation',
                                text: "",
                                tooltip: this.$t('buttons.activate.btnText'),
                                class: 'btn btn-success',
                                icon: "fas fa-lock-open",
                                show: this.hasDeleteRights,
                                clickHandler: this.activateBtnClick
                            }]
                    }
                };
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            deactivateBtnClick(args) {
                if (!this.hasDeleteRights) {
                    return this.$router.push(`/unauthorized`);
                }

                if (!args.active) {
                    this.$vs.notify({
                        title: this.$t('buttons.deactivate.action'),
                        text: this.$t('buttons.deactivate.already'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration,
                    })

                    return;
                }

                this.working = true;

                staticRegisterService
                    .deactivate(args.id)
                    .then(response => {
                        this.working = false;
                        this.refresh();

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .catch(error => {
                        this.working = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            activateBtnClick(args) {
                if (!this.hasDeleteRights) {
                    return this.$router.push(`/unauthorized`);
                }

                if (args.active) {
                    this.$vs.notify({
                        title: this.$t('buttons.activate.action'),
                        text: this.$t('buttons.activate.already'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration,
                    })

                    return;
                }

                this.working = true;

                staticRegisterService
                    .activate(args.id)
                    .then(response => {
                        this.working = false;
                        this.refresh();

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .catch(error => {
                        this.working = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            datasorceRequestEnd(e) {

            },
            createBtnClick() {
                if (!this.hasCreateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                this.showCreateModal();
            },
            showCreateModal() {
                this.showCreate = true;
            },
            showEditModal(e) {
                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                this.model = { ...e };
                this.showEdit = true;
            },
            onCreateClose() {
                this.showCreate = false;
            },
            onEditClose() {
                this.showEdit = false;
            },
            onCreate(model) {
                this.refresh();
            },
            onPersonEdit(item) {
                this.refresh();
            },
            refresh() {
                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.read();
                }
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
        },
        beforeMount() {
        },
        mounted: function () {
            var container = $(this.$el);
            container.find('[data-toggle="tooltip"]').tooltip({ container });
        },
        computed: {
            ...mapGetters(['gridOptions']),
            token: function () {
                return this.$store.getters.authToken;
            },
            hasReadRights() {
                return this.$store.getters.claims.dynamicRegister.read;
            },
            hasCreateRights() {
                return this.$store.getters.claims.dynamicRegister.create;
            },
            hasUpdateRights() {
                return this.$store.getters.claims.dynamicRegister.update;
            },
            hasDeleteRights() {
                return this.$store.getters.claims.dynamicRegister.delete;
            },
        }
    }
</script>

<style scoped>
</style>
